// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import {
  Anchor,
  Button,
  Checkbox,
  Grid,
  Group,
  Input,
  Paper,
  Radio,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { isEmail, useForm } from '@mantine/form';
import type { Questionnaire, QuestionnaireItem, QuestionnaireResponse } from '@medplum/fhirtypes';
import {
  buildQuestionnaireResponse,
  isUsPhone10,
  normalizeUsPhoneToE164,
  type PayerOption,
} from '@polarissleep/shared';
import dayjs from 'dayjs';
import { type JSX, useRef, useState } from 'react';
import { IMaskInput } from 'react-imask';
import { type ConsentRelationship, InformedConsentModal } from '../../components/InformedConsentModal';
import { getDeviceTimezone } from '../../utils/patient-timezone';
import { US_TIMEZONES } from '../settings/timezones';

interface SelfReferralFormProps {
  questionnaire: Questionnaire;
  onSubmit: (
    response: QuestionnaireResponse,
    signature?: string,
    agreementPdfBase64?: string,
    consentRelationship?: ConsentRelationship
  ) => void;
  onInsuranceSearch?: (query: string) => Promise<PayerOption[]>;
  submitting?: boolean;
  initialValues?: Partial<SelfReferralFormValues>;
  onValuesChange?: (values: SelfReferralFormValues) => void;
}

export interface SelfReferralFormValues {
  'first-name': string;
  'last-name': string;
  email: string;
  phone: string;
  'preferred-communication-method': string[];
  'sex-at-birth': string;
  pronouns: string;
  'pronouns-other': string;
  'primary-language': string;
  'primary-language-other': string;
  'insurance-type': string;
  'insurance-provider-id': string;
  'insurance-plan-name': string;
  'member-id': string;
  'group-number': string;
  policyholder: string;
  'secondary-plan-name': string;
  'secondary-member-id': string;
  'date-of-birth': string;
  'street-address': string;
  'address-line-2': string;
  city: string;
  state: string;
  'zip-code': string;
  timezone: string;
  'treatment-reason': string;
  'other-reason': string;
  'consent-signed': boolean;
  'informed-consent-signature': string;
  'informed-consent-agreement-pdf': string;
  'consent-relationship': ConsentRelationship | '';
  'consent-terms': boolean;
  'consent-to-contact': boolean;
}

const ZIP_REGEX = /^\d{5}(-\d{4})?$/;
const OTHER_REASON_OPTION = 'Other (please specify)';

function buildTimezoneOptions(deviceTimezone: string): typeof US_TIMEZONES {
  if (!deviceTimezone || US_TIMEZONES.some((option) => option.value === deviceTimezone)) {
    return US_TIMEZONES;
  }
  return [...US_TIMEZONES, { value: deviceTimezone, label: deviceTimezone.replaceAll('_', ' ') }];
}

function getItem(questionnaire: Questionnaire, linkId: string): QuestionnaireItem | undefined {
  return questionnaire.item?.find((i) => i.linkId === linkId);
}

function getStringOptions(item?: QuestionnaireItem): string[] {
  return (item?.answerOption ?? []).map((opt) => opt.valueString).filter((s): s is string => typeof s === 'string');
}

function getCodingOptions(item?: QuestionnaireItem): { value: string; label: string }[] {
  return (item?.answerOption ?? [])
    .filter((opt) => opt.valueCoding)
    .map((opt) => ({
      value: opt.valueCoding?.code ?? '',
      label: opt.valueCoding?.display ?? opt.valueCoding?.code ?? '',
    }));
}

export function SelfReferralForm({
  questionnaire,
  onSubmit,
  onInsuranceSearch,
  submitting,
  initialValues,
  onValuesChange,
}: SelfReferralFormProps): JSX.Element {
  const deviceTimezone = getDeviceTimezone();
  const timezoneOptions = buildTimezoneOptions(deviceTimezone);
  const commOptions = getCodingOptions(getItem(questionnaire, 'preferred-communication-method'));
  const sexOptions = getCodingOptions(getItem(questionnaire, 'sex-at-birth'));
  const pronounOptions = getCodingOptions(getItem(questionnaire, 'pronouns'));
  const languageOptions = getCodingOptions(getItem(questionnaire, 'primary-language'));
  const stateOptions = getCodingOptions(getItem(questionnaire, 'state'));
  const reasonOptions = getStringOptions(getItem(questionnaire, 'treatment-reason'));
  const insuranceOptions = getStringOptions(getItem(questionnaire, 'insurance-provider'));
  const [remotePayers, setRemotePayers] = useState<PayerOption[]>([]);
  const [searchUnavailable, setSearchUnavailable] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const searchRequest = useRef(0);
  const searchableInsuranceOptions = [
    ...remotePayers.map((payer) => ({ value: payer.id, label: payer.display })),
    ...insuranceOptions.map((option) => ({ value: option, label: option })),
  ].filter((option, index, options) => options.findIndex((candidate) => candidate.label === option.label) === index);
  const insuranceTypeOptions = [
    { value: 'Self-pay', label: 'Self-pay' },
    ...searchableInsuranceOptions.map((option) =>
      option.label === 'Other' ? { ...option, label: 'Other plan (not listed)' } : option
    ),
  ];

  const hasSelfPayToggle = insuranceOptions.length > 0;
  const insuranceDeferredBillingEnabled =
    import.meta.env.VITE_PATIENT_APP_INSURANCE_DEFERRED_BILLING_ENABLED === 'true';
  const insuredCopayPilotEnabled = import.meta.env.VITE_PATIENT_APP_INSURED_COPAY_PILOT_ENABLED === 'true';

  const form = useForm<SelfReferralFormValues>({
    initialValues: {
      'first-name': '',
      'last-name': '',
      email: '',
      phone: '',
      'preferred-communication-method': [],
      'sex-at-birth': '',
      pronouns: '',
      'pronouns-other': '',
      'primary-language': '',
      'primary-language-other': '',
      'insurance-type': '',
      'insurance-provider-id': '',
      'insurance-plan-name': '',
      'member-id': '',
      'group-number': '',
      policyholder: '',
      'secondary-plan-name': '',
      'secondary-member-id': '',
      'date-of-birth': '',
      'street-address': '',
      'address-line-2': '',
      city: '',
      state: '',
      'zip-code': '',
      timezone: deviceTimezone,
      'treatment-reason': '',
      'other-reason': '',
      'consent-signed': false,
      'informed-consent-signature': '',
      'informed-consent-agreement-pdf': '',
      'consent-relationship': '',
      'consent-terms': false,
      'consent-to-contact': false,
      ...initialValues,
    },
    onValuesChange,
    validate: {
      'first-name': (v) => (v.trim() ? null : 'First name is required'),
      'last-name': (v) => (v.trim() ? null : 'Last name is required'),
      email: isEmail('Enter a valid email address'),
      phone: (v) => (isUsPhone10(v) ? null : 'Enter a valid 10-digit US phone number'),
      'preferred-communication-method': (v) => (v.length > 0 ? null : 'Select at least one communication method'),
      'sex-at-birth': (v) => (v ? null : 'Sex at birth is required'),
      'pronouns-other': (v, values) =>
        values.pronouns === 'other' && !v.trim() ? 'Please specify your pronouns' : null,
      'primary-language': (v) => (v ? null : 'Primary language is required'),
      'primary-language-other': (v, values) =>
        values['primary-language'] === 'other' && !v.trim() ? 'Please specify your primary language' : null,
      'insurance-type': (v) => (hasSelfPayToggle && !v ? 'Insurance type is required' : null),
      'member-id': (v, values) =>
        hasSelfPayToggle && values['insurance-type'] && values['insurance-type'] !== 'Self-pay' && !v.trim()
          ? 'Member ID is required'
          : null,
      'insurance-plan-name': (v, values) =>
        values['insurance-type'] === 'Other' && !v.trim() ? 'Insurance plan name is required' : null,
      policyholder: (v, values) =>
        hasSelfPayToggle && values['insurance-type'] && values['insurance-type'] !== 'Self-pay' && !v
          ? 'Please select the policyholder'
          : null,
      'date-of-birth': (v) => {
        if (!v || v.length < 10) {
          return 'Date of birth is required';
        }
        const parsed = dayjs(v, 'MM/DD/YYYY', true);
        if (!parsed.isValid()) {
          return 'Enter a valid date (MM/DD/YYYY)';
        }
        if (parsed.isAfter(dayjs())) {
          return 'Date of birth cannot be in the future';
        }
        if (dayjs().diff(parsed, 'year') < 3) {
          return "We're sorry — Polaris Sleep currently can only see patients 3 or older.";
        }
        return null;
      },
      'street-address': (v) => (v.trim() ? null : 'Street address is required'),
      city: (v) => (v.trim() ? null : 'City is required'),
      state: (v) => {
        if (!v) {
          return 'State is required';
        }
        if (!['Maryland', 'District of Columbia', 'Virginia', 'Texas', 'Indiana', 'Georgia'].includes(v)) {
          return "We're sorry — Polaris Sleep currently serves Maryland, Washington, D.C., Virginia, Texas, Indiana, and Georgia only.";
        }
        return null;
      },
      'zip-code': (v) => (ZIP_REGEX.test(v.trim()) ? null : 'Enter a valid US ZIP code'),
      'treatment-reason': (v) => (v ? null : 'Reason for seeking treatment is required'),
      'other-reason': (v, values) =>
        values['treatment-reason'] === OTHER_REASON_OPTION && !v.trim() ? 'Please specify the other reason' : null,
      'consent-signed': (v) => (v ? null : 'Please review and sign the informed consent'),
      'consent-terms': (v) => (v ? null : 'You must accept the Terms of Use and Privacy Policy'),
      'consent-to-contact': (v) => (v ? null : 'You must consent to be contacted'),
    },
  });

  const focusFirstError = () => {
    requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>('[aria-invalid="true"]');
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el?.focus();
    });
  };

  const handleSubmit = form.onSubmit((values) => {
    const isSelfPay = hasSelfPayToggle ? values['insurance-type'] === 'Self-pay' : true;
    const qrValues: Record<string, unknown> = {
      'first-name': values['first-name'].trim(),
      'last-name': values['last-name'].trim(),
      email: values.email.trim(),
      phone: normalizeUsPhoneToE164(values.phone) ?? values.phone,
      'preferred-communication-method': values['preferred-communication-method'],
      'sex-at-birth': values['sex-at-birth'],
      'self-pay': isSelfPay,
      'date-of-birth': values['date-of-birth']
        ? dayjs(values['date-of-birth'], 'MM/DD/YYYY').format('YYYY-MM-DD')
        : undefined,
      'street-address': values['street-address'].trim(),
      'address-line-2': values['address-line-2'].trim(),
      city: values.city.trim(),
      state: values.state,
      'zip-code': values['zip-code'].trim(),
      'treatment-reason': values['treatment-reason'],
      'consent-signed': values['consent-signed'],
      'consent-terms': values['consent-terms'],
      'consent-to-contact': values['consent-to-contact'],
    };
    if (values.pronouns) {
      qrValues['pronouns'] = values.pronouns;
    }
    if (values.pronouns === 'other' && values['pronouns-other'].trim()) {
      qrValues['pronouns-other'] = values['pronouns-other'].trim();
    }
    qrValues['primary-language'] = values['primary-language'];
    if (values['primary-language'] === 'other' && values['primary-language-other'].trim()) {
      qrValues['primary-language-other'] = values['primary-language-other'].trim();
    }
    if (hasSelfPayToggle && !isSelfPay) {
      qrValues['insurance-provider'] = values['insurance-type'];
      if (values['insurance-provider-id']) {
        qrValues['insurance-provider-id'] = values['insurance-provider-id'];
      }
      qrValues['member-id'] = values['member-id'].trim();
      qrValues['group-number'] = values['group-number'].trim();
      qrValues.policyholder = values.policyholder;
      qrValues['secondary-plan-name'] = values['secondary-plan-name'].trim();
      qrValues['secondary-member-id'] = values['secondary-member-id'].trim();
      if (values['insurance-type'] === 'Other') {
        qrValues['insurance-plan-name'] = values['insurance-plan-name'].trim();
      }
    }
    if (values.timezone) {
      qrValues.timezone = values.timezone;
    }
    if (values['address-line-2'].trim() === '') {
      delete qrValues['address-line-2'];
    }
    if (values['treatment-reason'] === OTHER_REASON_OPTION) {
      qrValues['other-reason'] = values['other-reason'].trim();
    }
    onSubmit(
      buildQuestionnaireResponse(questionnaire, qrValues),
      values['informed-consent-signature'] || undefined,
      values['informed-consent-agreement-pdf'] || undefined,
      values['consent-relationship'] || undefined
    );
  }, focusFirstError);

  const showPronounsOther = form.values.pronouns === 'other';
  const showLanguageOther = form.values['primary-language'] === 'other';
  const showOtherReason = form.values['treatment-reason'] === OTHER_REASON_OPTION;
  const [consentModalOpen, setConsentModalOpen] = useState(false);
  const consentLabel =
    getItem(questionnaire, 'consent-to-contact')?.text ?? 'I consent to be contacted by Polaris Sleep';

  return (
    <>
      <InformedConsentModal
        opened={consentModalOpen}
        onClose={() => setConsentModalOpen(false)}
        dateOfBirth={form.values['date-of-birth']}
        onConfirm={(signature, agreementPdfBase64, relationship) => {
          form.setFieldValue('consent-signed', true);
          form.setFieldValue('informed-consent-signature', signature);
          form.setFieldValue('informed-consent-agreement-pdf', agreementPdfBase64 ?? '');
          form.setFieldValue('consent-relationship', relationship ?? 'Self');
          setConsentModalOpen(false);
        }}
        submitting={false}
      />
      <form onSubmit={handleSubmit} noValidate>
        <Stack gap="sm">
          <Group grow>
            <TextInput label="First Name" withAsterisk {...form.getInputProps('first-name')} />
            <TextInput label="Last Name" withAsterisk {...form.getInputProps('last-name')} />
          </Group>

          <Group grow>
            <Input.Wrapper label="Date of Birth" withAsterisk {...form.getInputProps('date-of-birth')}>
              <Input
                component={IMaskInput}
                mask="00/00/0000"
                placeholder="MM/DD/YYYY"
                {...form.getInputProps('date-of-birth')}
              />
            </Input.Wrapper>
            <Select
              label="Sex at Birth"
              data={sexOptions}
              withAsterisk
              searchable={false}
              {...form.getInputProps('sex-at-birth')}
            />
          </Group>

          <Group grow>
            <TextInput
              label="Email address"
              inputMode="email"
              autoComplete="email"
              placeholder="patient@example.com"
              withAsterisk
              {...form.getInputProps('email')}
            />
            <Input.Wrapper label="Phone number" withAsterisk {...form.getInputProps('phone')}>
              <Input
                component={IMaskInput}
                mask="(000) 000-0000"
                placeholder="(555) 555-5555"
                {...form.getInputProps('phone')}
              />
            </Input.Wrapper>
          </Group>

          <Checkbox.Group
            label="Preferred contact method"
            description="How should we contact you? Select all that apply."
            withAsterisk
            {...form.getInputProps('preferred-communication-method')}
          >
            <Stack gap="xs" mt="xs">
              {commOptions.map((opt) => (
                <Checkbox key={opt.value} value={opt.value} label={opt.label} />
              ))}
            </Stack>
          </Checkbox.Group>

          <Group grow>
            <Select
              label="Pronouns (optional)"
              data={pronounOptions}
              searchable={false}
              clearable
              {...form.getInputProps('pronouns')}
            />
            <Select
              label="Primary Language"
              data={languageOptions}
              withAsterisk
              searchable={false}
              {...form.getInputProps('primary-language')}
            />
          </Group>
          {showPronounsOther && (
            <TextInput label="Please specify your pronouns" withAsterisk {...form.getInputProps('pronouns-other')} />
          )}
          {showLanguageOther && (
            <TextInput
              label="Please specify your primary language"
              withAsterisk
              {...form.getInputProps('primary-language-other')}
            />
          )}

          <TextInput
            label="Home Address"
            description="Which state do you live in?"
            placeholder="Street Address"
            withAsterisk
            {...form.getInputProps('street-address')}
          />
          <TextInput placeholder="Apt, unit, suite (optional)" {...form.getInputProps('address-line-2')} />
          <Grid>
            <Grid.Col span={6}>
              <TextInput label="City" placeholder="City" withAsterisk {...form.getInputProps('city')} />
            </Grid.Col>
            <Grid.Col span={3}>
              <Select
                label="State"
                data={stateOptions}
                placeholder="State"
                searchable
                withAsterisk
                {...form.getInputProps('state')}
              />
            </Grid.Col>
            <Grid.Col span={3}>
              <TextInput label="ZIP Code" placeholder="12345" withAsterisk {...form.getInputProps('zip-code')} />
            </Grid.Col>
          </Grid>
          <Select
            label="Time Zone (optional)"
            data={timezoneOptions}
            clearable
            searchable
            placeholder="Select your time zone"
            {...form.getInputProps('timezone')}
          />

          <Select
            label="Reason for seeking treatment"
            data={reasonOptions}
            withAsterisk
            {...form.getInputProps('treatment-reason')}
          />
          {showOtherReason && (
            <TextInput
              label="Please specify"
              placeholder="Describe the reason"
              withAsterisk
              {...form.getInputProps('other-reason')}
            />
          )}

          {hasSelfPayToggle && (
            <>
              <Select
                label="Insurance type"
                description="Choose the plan you'd like to bill."
                data={insuranceTypeOptions}
                searchable
                nothingFoundMessage={searchUnavailable ? 'Search unavailable — choose Other' : 'No plans found'}
                withAsterisk
                {...form.getInputProps('insurance-type')}
                onSearchChange={(query) => {
                  if (searchTimer.current) {
                    clearTimeout(searchTimer.current);
                  }
                  if (!onInsuranceSearch || query.trim().length < 2) {
                    setRemotePayers([]);
                    setSearchUnavailable(false);
                    return;
                  }
                  const requestId = ++searchRequest.current;
                  searchTimer.current = setTimeout(() => {
                    onInsuranceSearch(query)
                      .then((payers) => {
                        if (requestId === searchRequest.current) {
                          setRemotePayers(payers);
                          setSearchUnavailable(false);
                        }
                      })
                      .catch(() => {
                        if (requestId === searchRequest.current) {
                          setRemotePayers([]);
                          setSearchUnavailable(true);
                        }
                      });
                  }, 250);
                }}
                onChange={(value, option) => {
                  const display = value === 'Other' ? 'Other' : (option?.label ?? '');
                  const payer = remotePayers.find((candidate) => candidate.id === value);
                  form.setFieldValue('insurance-type', display);
                  form.setFieldValue('insurance-provider-id', payer?.id ?? '');
                  if (!payer && display !== 'Self-pay' && display !== 'Other' && onInsuranceSearch) {
                    onInsuranceSearch(display)
                      .then((payers) => {
                        const matchingPayer = payers.find((candidate) => candidate.display === display);
                        if (form.values['insurance-type'] === display) {
                          form.setFieldValue('insurance-provider-id', matchingPayer?.id ?? '');
                        }
                      })
                      .catch(() => undefined);
                  }
                }}
              />
              {form.values['insurance-type'] === 'Self-pay' && (
                <Paper p="xl" radius="md" bg="polaris.0" withBorder>
                  <Text fw={700} fz={28} c="polaris.5" lh={1.2}>
                    $400{' '}
                    <Text span fw={600} fz="sm" c="polaris.5">
                      for your complete sleep evaluation
                    </Text>
                  </Text>
                  <Text size="md" mt="xs">
                    Your $400 covers everything you need to get answers — from your initial intake through your home
                    sleep test and a dedicated appointment with one of our sleep specialists to review your results and
                    build a treatment plan together.
                  </Text>
                  <Text size="sm" c="dimmed" mt="xs">
                    You won't be charged until we've confirmed a home sleep test is right for you. If it's not, we'll
                    refer you to one of our in-lab partners. Follow-up appointments, if needed, are billed separately.
                  </Text>
                </Paper>
              )}
              {form.values['insurance-type'] && form.values['insurance-type'] !== 'Self-pay' && (
                <>
                  {form.values['insurance-type'] === 'Other' && (
                    <Paper p="xl" radius="md" bg="polaris.0" withBorder>
                      <Text fw={700} fz={28} c="polaris.5" lh={1.2}>
                        We'll check your coverage{' '}
                        <Text span fw={600} fz="sm" c="polaris.5">
                          before anything is billed
                        </Text>
                      </Text>
                      <Text size="md" mt="xs">
                        Once you submit, our team verifies your eligibility and will get back to you shortly.
                      </Text>
                      <Text size="sm" c="dimmed" mt="xs">
                        You won't be charged while we verify. If your plan doesn't cover an at-home sleep test, we'll
                        let you know your options, including self-pay.
                      </Text>
                    </Paper>
                  )}
                  {form.values['insurance-type'] !== 'Other' && insuranceDeferredBillingEnabled && (
                    <Paper p="xl" radius="md" bg="polaris.0" withBorder>
                      <Text fw={700} fz={28} c="polaris.5" lh={1.2}>
                        No upfront cost{' '}
                        <Text span fw={600} fz="sm" c="polaris.5">
                          with insurance
                        </Text>
                      </Text>
                      <Text size="md" mt="xs">
                        After you book, we'll email you an estimate of what you may owe. We'll bill your insurance, and
                        your plan will decide the final amount after reviewing the claim. We'll bill you for any amount
                        you owe after the claim is complete.
                      </Text>
                      <Text size="sm" c="dimmed" mt="xs">
                        What you pay depends on your plan. Call your insurance if you have questions about your
                        benefits.
                      </Text>
                    </Paper>
                  )}
                  {form.values['insurance-type'] !== 'Other' &&
                    !insuranceDeferredBillingEnabled &&
                    insuredCopayPilotEnabled && (
                      <Paper p="xl" radius="md" bg="polaris.0" withBorder>
                        <Text fw={700} fz={28} c="polaris.5" lh={1.2}>
                          $50{' '}
                          <Text span fw={600} fz="sm" c="polaris.5">
                            copay for your complete sleep evaluation
                          </Text>
                        </Text>
                        <Text size="md" mt="xs">
                          Your $50 covers everything you need to get answers — from your initial intake through your
                          home sleep test and a dedicated appointment with one of our sleep specialists to review your
                          results and build a treatment plan together.
                        </Text>
                        <Text size="sm" c="dimmed" mt="xs">
                          You won't be charged until we've confirmed a home sleep test is right for you. If it's not,
                          we'll refer you to one of our in-lab partners. Follow-up appointments, if needed, are billed
                          separately.
                        </Text>
                      </Paper>
                    )}
                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        label="Member ID"
                        description="Found on your insurance card."
                        placeholder="XJL884203107"
                        withAsterisk
                        {...form.getInputProps('member-id')}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        label="Group number"
                        description="Optional — if your card shows one."
                        placeholder="Optional"
                        {...form.getInputProps('group-number')}
                      />
                    </Grid.Col>
                  </Grid>
                  <Radio.Group label="Who is the policyholder?" withAsterisk {...form.getInputProps('policyholder')}>
                    <Group mt="xs">
                      <Radio value="self" label="Myself" />
                      <Radio value="spouse" label="Spouse or partner" />
                      <Radio value="parent" label="Parent or guardian" />
                    </Group>
                  </Radio.Group>
                  <Stack gap="xs">
                    <Text fw={600}>Secondary coverage</Text>
                    <Text size="sm" c="dimmed">
                      Optional — add a second plan if you have one.
                    </Text>
                    <Grid>
                      <Grid.Col span={{ base: 12, sm: 6 }}>
                        <TextInput
                          aria-label="Secondary plan name"
                          placeholder="Secondary plan name"
                          {...form.getInputProps('secondary-plan-name')}
                        />
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, sm: 6 }}>
                        <TextInput
                          aria-label="Secondary coverage ID"
                          placeholder="Secondary member ID"
                          {...form.getInputProps('secondary-member-id')}
                        />
                      </Grid.Col>
                    </Grid>
                  </Stack>
                  {form.values['insurance-type'] === 'Other' && (
                    <TextInput
                      label="Insurance plan name"
                      description="Exactly as it appears on the front of your card."
                      placeholder="e.g. Johns Hopkins Advantage MD"
                      withAsterisk
                      {...form.getInputProps('insurance-plan-name')}
                    />
                  )}
                </>
              )}
            </>
          )}

          <Paper withBorder p="md" radius="md">
            <Stack gap="xs">
              <Text fw={600}>Informed consent for telehealth</Text>
              <Text size="sm" c="dimmed">
                This explains the risks, benefits, alternatives, and limitations of using telehealth to receive medical
                care. You can receive care from the convenience of your home.
              </Text>
              {form.values['consent-signed'] ? (
                <Text size="sm" c="green" fw={500}>
                  ✓ Signed
                </Text>
              ) : (
                <Button variant="default" size="xs" w="fit-content" onClick={() => setConsentModalOpen(true)}>
                  Review &amp; sign
                </Button>
              )}
              {form.errors['consent-signed'] && (
                <Text size="xs" c="red">
                  {form.errors['consent-signed']}
                </Text>
              )}
            </Stack>
          </Paper>
          <Checkbox
            label={
              <>
                I consent to Polaris Sleep's{' '}
                <Anchor
                  href="https://www.polarissleep.com/terms-of-use"
                  target="_blank"
                  rel="noopener noreferrer"
                  size="sm"
                >
                  Terms of Use
                </Anchor>{' '}
                and{' '}
                <Anchor href="https://www.polarissleep.com/privacy" target="_blank" rel="noopener noreferrer" size="sm">
                  Privacy Policy
                </Anchor>
                .
              </>
            }
            {...form.getInputProps('consent-terms', { type: 'checkbox' })}
          />
          <Checkbox label={consentLabel} {...form.getInputProps('consent-to-contact', { type: 'checkbox' })} />

          <Group justify="flex-end" mt="md">
            <Button type="submit" loading={submitting} disabled={submitting}>
              {submitting ? 'Submitting…' : 'Continue'}
            </Button>
          </Group>
        </Stack>
      </form>
    </>
  );
}
